/**
 * Browser-session plumbing for the dashboard (VAPT "Improper Session Expiration").
 *
 * - Tokens live in sessionStorage only (per tab, gone when the browser closes). The
 *   refresh token used to sit in localStorage for a year.
 * - Tabs share one session over a BroadcastChannel: a new tab asks an open one for the
 *   tokens, every refresh is broadcast, and a sign-out in one tab signs out all.
 * - Every exit (sign-out, idle, expiry, revoked) goes through clearSessionData(), which
 *   wipes everything tied to the user and keeps only harmless device preferences.
 *
 * The server side is AuthSession in quik-core-v2: 15-min access token, 30-min
 * single-use refresh token, 12-hour cap, revocable.
 */
import { getApps } from 'firebase/app';
import { getAuth, signOut } from 'firebase/auth';

export const SESSION_PLATFORM = 'DASHBOARD';

// Why the last session ended — read once by the login page.
export type SessionEndReason = 'logout' | 'idle' | 'expired';

const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';
const OBTAINED_AT_KEY = 'tokensObtainedAt';
const END_REASON_KEY = 'sessionEndReason';
const CHANNEL_NAME = 'quikstep-dashboard-session';
const PEER_REPLY_TIMEOUT_MS = 400;
// Once one peer has answered, how long to wait for the others.
const PEER_SETTLE_MS = 50;

// Device/UI preferences that are not about the signed-in user — they survive a
// sign-out. Everything else in localStorage is wiped.
const DEVICE_PREFERENCE_KEYS = [
  'appLang',
  'able-pro-material-react-ts-config',
  'kanban-zoom-level',
  'chat-fab-pos',
  'fcm-permission-dismissed',
  'deviceId',
  // Transactions page: table/kanban view, the filter bar (client, team, status,
  // dates) and the search-target picker. Kept so the page comes back the way it was
  // left. These DO hold the last user's selections, so the next user to sign in on
  // this browser inherits them until they pick their own.
  'viewMode',
  'dashboardFilters',
  'txnSearchTarget'
];

export interface SessionTokens {
  token: string;
  refreshToken: string;
  // When this browser received the pair from the server (ms). Orders pairs issued in
  // the same second, which the tokens' own `iat` cannot.
  obtainedAt?: number;
}

const safe = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

/** Claims of a JWT, without verifying it (the server does that). */
export const decodeJwt = (jwt: string | null | undefined): Record<string, any> | null => {
  if (!jwt) return null;
  return safe(() => {
    const part = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(part + '='.repeat((4 - (part.length % 4)) % 4)));
  }, null);
};

/** Milliseconds until the token expires (negative once expired, -Infinity if unreadable). */
export const msUntilExpiry = (jwt: string | null | undefined): number => {
  const exp = decodeJwt(jwt)?.exp;
  return typeof exp === 'number' ? exp * 1000 - Date.now() : -Infinity;
};

export const getStoredTokens = (): SessionTokens | null => {
  const token = safe(() => sessionStorage.getItem(TOKEN_KEY), null);
  const refreshToken = safe(() => sessionStorage.getItem(REFRESH_TOKEN_KEY), null);
  const obtainedAt = Number(safe(() => sessionStorage.getItem(OBTAINED_AT_KEY), null)) || undefined;
  return token && refreshToken ? { token, refreshToken, obtainedAt } : null;
};

export const getRefreshToken = (): string | null => safe(() => sessionStorage.getItem(REFRESH_TOKEN_KEY), null);

export const hasSession = (): boolean => !!getRefreshToken();

export const storeTokens = ({ token, refreshToken, obtainedAt }: SessionTokens) => {
  safe(() => {
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    if (obtainedAt) sessionStorage.setItem(OBTAINED_AT_KEY, String(obtainedAt));
    else sessionStorage.removeItem(OBTAINED_AT_KEY);
  }, undefined);
};

/** Older builds kept a year-long refresh token in localStorage; it is never used again. */
export const dropLegacyRefreshToken = () => {
  safe(() => {
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('serviceToken');
    localStorage.removeItem('users');
  }, undefined);
};

export const setSessionEndReason = (reason: SessionEndReason) => {
  safe(() => sessionStorage.setItem(END_REASON_KEY, reason), undefined);
};

/** Why the previous session ended (for the login page), or null. */
export const readSessionEndReason = (): SessionEndReason | null =>
  safe(() => sessionStorage.getItem(END_REASON_KEY), null) as SessionEndReason | null;

export const clearSessionEndReason = () => {
  safe(() => sessionStorage.removeItem(END_REASON_KEY), undefined);
};

// Single source of truth for idle-sign-out timing: ApiContext passes these ms values
// to the actual timer, and the text below is derived from them, so the two can never
// drift out of sync the way a hand-written "N minutes" string can.
export const IDLE_TIMEOUT_MS = 60 * 60 * 1000; // sign out after this long without activity in any tab
export const IDLE_WARNING_MS = 5 * 60 * 1000; // warn this long before signing out

export const formatDuration = (ms: number): string => {
  const minutes = Math.round(ms / 60000);
  if (minutes > 0 && minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
};

export const SESSION_END_MESSAGES: Partial<Record<SessionEndReason, string>> = {
  idle: `You were signed out after ${formatDuration(IDLE_TIMEOUT_MS)} of inactivity. Please sign in again.`,
  expired: 'Your session has expired. Please sign in again.'
};

/** Remove user data from localStorage (selected client, filters, views, caches); device preferences stay. */
export const clearStoredUserData = () => {
  safe(() => {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !DEVICE_PREFERENCE_KEYS.includes(key)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  }, undefined);
};

/** Sign this tab out of Firebase (its sign-in only lives in this tab). Best-effort. */
export const signOutOfFirebase = async () => {
  try {
    const app = getApps()[0];
    if (app) await signOut(getAuth(app));
  } catch {
    // Nothing more to do: the sign-in is gone with the tab anyway.
  }
};

/**
 * Wipe everything tied to the signed-in user from this browser: tokens, the selected
 * client, filters and views, the translation cache, chat state, the Microsoft sign-in
 * cache (msal.*) and the Firebase sign-in. Device preferences survive.
 */
export const clearSessionData = async (reason?: SessionEndReason) => {
  safe(() => sessionStorage.clear(), undefined);
  clearStoredUserData();
  await signOutOfFirebase();
  if (reason && reason !== 'logout') setSessionEndReason(reason);
};

// ---------------------------------------------------------------- cross-tab sync

type SessionMessage = { type: 'request' } | { type: 'share'; tokens: SessionTokens } | { type: 'signed-out'; reason: SessionEndReason };

let channel: BroadcastChannel | null = null;
const getChannel = (): BroadcastChannel | null => {
  if (channel) return channel;
  if (typeof BroadcastChannel === 'undefined') return null;
  channel = safe(() => new BroadcastChannel(CHANNEL_NAME), null);
  return channel;
};

const post = (message: SessionMessage) => {
  safe(() => getChannel()?.postMessage(message), undefined);
};

/** The server-side session (AuthSession sid) a token pair belongs to. */
const sessionIdOf = (tokens: SessionTokens | null | undefined): string | undefined => decodeJwt(tokens?.token)?.sid;

/** Tell the other tabs about tokens this tab just obtained. */
export const shareTokens = (tokens: SessionTokens) => post({ type: 'share', tokens });

/** Tell the other tabs this session is over. */
export const announceSignOut = (reason: SessionEndReason) => post({ type: 'signed-out', reason });

/**
 * Whether a peer's tokens should replace this tab's: always when this tab has none,
 * otherwise only newer tokens of the SAME session. Another tab signed in to a
 * different session (e.g. another account) never takes this tab over.
 */
const isNewer = (candidate: SessionTokens, current: SessionTokens | null) => {
  if (!current) return true;
  if (sessionIdOf(candidate) !== sessionIdOf(current)) return false;
  const issuedLater = (decodeJwt(candidate.token)?.iat ?? 0) - (decodeJwt(current.token)?.iat ?? 0);
  if (issuedLater !== 0) return issuedLater > 0;
  // Issued in the same second (two tabs renewing back to back): the pair this browser
  // received later is the newer one — refreshes are serialised across tabs. Keeping
  // the older one would present a retired refresh token at the next renewal, which
  // the server treats as reuse and ends the session for every tab.
  return (candidate.obtainedAt ?? 0) > (current.obtainedAt ?? 0);
};

// ---------------------------------------------------------------- tab presence

// Each open dashboard tab holds a Web Lock named after it for its whole lifetime (the
// browser releases it when the tab closes). Listing the held locks tells a tab, at
// once, whether any other tab could answer it — so a lone tab never waits out
// PEER_REPLY_TIMEOUT_MS for replies that cannot come.
const TAB_LOCK_PREFIX = 'quikstep-dashboard-tab:';
const TAB_LOCK_NAME = TAB_LOCK_PREFIX + Math.random().toString(36).slice(2) + Date.now().toString(36);
let presenceAnnounced = false;

const announcePresence = () => {
  const locks = (navigator as any)?.locks;
  if (presenceAnnounced || !locks?.request) return;
  presenceAnnounced = true;
  locks.request(TAB_LOCK_NAME, () => new Promise<void>(() => undefined)).catch(() => undefined);
};

/** Whether another dashboard tab is open (assumed yes when the browser can't say). */
const otherTabsOpen = async (): Promise<boolean> => {
  const locks = (navigator as any)?.locks;
  if (!locks?.query) return true;
  try {
    const { held = [] } = await locks.query();
    return held.some((lock: { name?: string }) => !!lock.name && lock.name.startsWith(TAB_LOCK_PREFIX) && lock.name !== TAB_LOCK_NAME);
  } catch {
    return true;
  }
};

/**
 * Ask the open tabs for the newest tokens. Resolves with them if a tab has tokens
 * newer than this tab's (or this tab has none), else null — at once when no other
 * tab is open, otherwise after a short wait.
 */
export const requestTokensFromPeers = async (): Promise<SessionTokens | null> => {
  announcePresence();
  const ch = getChannel();
  if (!ch || !(await otherTabsOpen())) return null;
  return new Promise((resolve) => {
    let settled = false;
    let timer: number | undefined;
    const finish = (tokens: SessionTokens | null) => {
      if (settled) return;
      settled = true;
      ch.removeEventListener('message', onMessage);
      window.clearTimeout(timer);
      resolve(tokens);
    };
    const onMessage = (event: MessageEvent<SessionMessage>) => {
      const message = event.data;
      if (message?.type !== 'share' || !message.tokens) return;
      if (isNewer(message.tokens, getStoredTokens())) {
        finish(message.tokens);
        return;
      }
      // A peer answered with nothing newer than this tab has: give any other peer a
      // moment to answer, then stop waiting.
      window.clearTimeout(timer);
      timer = window.setTimeout(() => finish(null), PEER_SETTLE_MS);
    };
    timer = window.setTimeout(() => finish(null), PEER_REPLY_TIMEOUT_MS);
    ch.addEventListener('message', onMessage);
    post({ type: 'request' });
  });
};

/**
 * Answer peers' requests and apply their updates. `onTokens` gets newer tokens from
 * another tab; `onSignedOut` runs when another tab signed out. Returns an unsubscribe.
 */
export const listenToPeers = (handlers: {
  onTokens: (tokens: SessionTokens) => void;
  onSignedOut: (reason: SessionEndReason) => void;
}): (() => void) => {
  announcePresence();
  const ch = getChannel();
  if (!ch) return () => undefined;
  const onMessage = (event: MessageEvent<SessionMessage>) => {
    const message = event.data;
    if (!message) return;
    if (message.type === 'request') {
      const tokens = getStoredTokens();
      if (tokens) post({ type: 'share', tokens });
    } else if (message.type === 'share') {
      if (message.tokens && isNewer(message.tokens, getStoredTokens())) handlers.onTokens(message.tokens);
    } else if (message.type === 'signed-out') {
      handlers.onSignedOut(message.reason);
    }
  };
  ch.addEventListener('message', onMessage);
  return () => ch.removeEventListener('message', onMessage);
};

/** Run `task` with no other tab of this browser refreshing at the same time. */
export const withRefreshLock = async <T>(task: () => Promise<T>): Promise<T> => {
  const locks = (navigator as any)?.locks;
  if (locks?.request) return locks.request('quikstep-dashboard-refresh', task);
  return task();
};

// ---------------------------------------------------------------- refresh hook

// ApiContext registers its refresh routine here so the axios interceptor can renew an
// expired access token and retry, without importing the context (circular import).
type Refresher = () => Promise<string | null>;
let refresher: Refresher | null = null;
export const setSessionRefresher = (fn: Refresher | null) => {
  refresher = fn;
};
export const refreshAccessToken = (): Promise<string | null> => (refresher ? refresher() : Promise.resolve(null));
